import { useState } from "react";
import axios from "axios";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const client = axios.create({ baseURL: API });
const COPY = {
  cancel: { title: "Cancel this ticket?", verb: "Cancel ticket", done: "Ticket cancelled", cls: "bg-red-600 hover:bg-red-700", ph: "Reason for cancelling (optional)" },
  resolve: { title: "Mark this ticket resolved?", verb: "Resolve ticket", done: "Ticket resolved", cls: "bg-emerald-600 hover:bg-emerald-700", ph: "Any comment about the work (optional)" },
};

export function OtpActionDialog({ mode, token, ticketNumber, onClose, onDone }) {
  const c = COPY[mode] || COPY.cancel;
  const [otp, setOtp] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const sendOtp = async () => {
    setSending(true); setErr("");
    try { await client.post(`/public/ticket/${token}/send-otp`); toast.success("OTP sent to the resident's WhatsApp"); }
    catch (e) { setErr(apiError(e)); }
    finally { setSending(false); }
  };
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await client.post(`/public/ticket/${token}/${mode}`, { otp, comment });
      toast.success(c.done);
      onDone();
    } catch (e) { setErr(apiError(e)); setOtp(""); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid={`ticket-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{c.title}</DialogTitle>
          <DialogDescription>
            Enter the 6-digit OTP sent to the resident's WhatsApp for {ticketNumber}. If the vendor has accepted, the OTP already received works too.
          </DialogDescription>
        </DialogHeader>
        <Button variant="outline" size="sm" onClick={sendOtp} disabled={sending} data-testid={`ticket-${mode}-send-otp`}>
          {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Send OTP to my WhatsApp
        </Button>
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid={`ticket-${mode}-otp-input`}>
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
          </InputOTP>
        </div>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} rows={3}
          placeholder={c.ph} data-testid={`ticket-${mode}-comment`} />
        {err && <p className="text-center text-xs font-medium text-red-600" data-testid={`ticket-${mode}-error`}>{err}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid={`ticket-${mode}-back`}>Back</Button>
          <Button className={c.cls} disabled={busy || otp.length !== 6} onClick={submit} data-testid={`ticket-${mode}-confirm`}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{c.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
